'use client';

/**
 * `TradeExecutorProvider` — the single home for on-chain trade EXECUTION on a
 * market/winner page. It owns the buy/sell mutations, the login + first-trade
 * terms gate, the success confetti + viral-win celebration, and the one-tap
 * ("chip") progress lifecycle rendered by the position card.
 *
 * Both surfaces consume it:
 *  - `<TradeForm>` delegates its Buy/Sell submit here (swap stays in the form).
 *  - the position card's quick-trade chips call `executeBuy` / `executeSell`
 *    directly — so a chip executes self-contained, WITHOUT the visible trade
 *    form reacting, and still works on mobile where the form is unmounted (the
 *    sheet is not rendered when closed).
 *
 * The money math (estimate + sizing) is pure and lives in the shared
 * `use-trade-estimate` / `quick-trade-*` modules; this provider is the one place
 * the mutation dispatch, gates, and celebration live.
 */
import { c as _c } from "react/compiler-runtime";
import type { ActiveWallet, PositionAccountingDto } from '@morfi/backend-sdk-v2/client';
import { computeCheckedSellPositionAccounting, useActiveWallet, useAuth, useBuyTrade, useSdkTurnkey, useSellTrade, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import type BN from 'bn.js';
import { type Context, createContext, type PropsWithChildren, use, useEffect, useRef, useState } from 'react';
import { AcceptTradeTermsDialog } from '@/components/trade/accept-trade-terms-dialog';
import { useAllowRapidTrades } from '@/hooks/use-allow-rapid-trades';
import { captureReturnToAfterAuth } from '@/lib/auth/next-url';
import { runWithQuickTradeProgress } from '@/lib/trading/quick-trade-progress';
import { getExternalWalletProvider } from '@/lib/wallet/external-wallet-provider';
import { useRecoverTradeTelemetry } from '@/queries/recover-trade-telemetry';
import { useUnknownTradeStatus } from '@/queries/unknown-trade-status';
import type { AutoTradePending } from '@/stores/chart-settings';
import { useThesisDialogStore } from '@/stores/thesis-dialog';
import { isTradeSubmissionBlocked, submitGuardedTrade, useTradeSubmissionLockStore } from '@/stores/trade-submission-lock';
import { useTradeTermsAcceptanceStore } from '@/stores/trade-terms-acceptance';
import { useTradeCelebration } from './use-trade-celebration';

// ── Types ────────────────────────────────────────────────────────────────────

/** Viewport-normalised confetti origin (0–1 x/y). */
export interface ConfettiOrigin {
  x: number;
  y: number;
}

/** Buy-leg execution request. Wallet + signing deps are supplied by the provider. */
export interface ExecuteBuyArgs {
  marketAddress: string;
  outcomeIndex: number;
  outcomeMint: string;
  amountInD6: BN;
  walletDebitD6: BN;
  feeExcludedQuoteInputD6: BN;
  outcomeTokensOutD6: BN;
  desiredAmountD6: BN;
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
  /** Platform's share of the base trading fee (D6 string) for revenue analytics. */
  platformFeeD6?: string | undefined;
  /** Origin for the success burst (over the form, or over the card for a chip). */
  confettiOrigin?: ConfettiOrigin | undefined;
  /** Identity of the pressed chip, so the card can render its progress. Absent for form submits. */
  chip?: AutoTradePending | undefined;
}

/** Sell-leg execution request. The same exact amounts drive mutation and viral PnL. */
export interface ExecuteSellArgs {
  marketAddress: string;
  marketTitle?: string | undefined;
  outcomeIndex: number;
  outcomeMint: string;
  outcomeLabel: string;
  tokensNeededD6: BN;
  expectedNetUsdcOutD6: BN;
  grossQuoteAmountD6: BN;
  feeExcludedQuoteOutputD6: BN;
  /** Conviction penalty this sell pays (D6) — booked straight into the optimistic row's P&L. */
  sellPenaltyFeeD6: BN;
  positionAccounting: PositionAccountingDto | null;
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
  /** Platform's share of the base trading fee (D6 string) for revenue analytics. */
  platformFeeD6?: string | undefined;
  confettiOrigin?: ConfettiOrigin | undefined;
  chip?: AutoTradePending | undefined;
}
export interface TradeExecutor {
  /** Execute a buy (login + terms gated), arming confetti + the chip signal. */
  executeBuy: (args: ExecuteBuyArgs) => void;
  /** Execute a sell (login + terms gated), capturing the viral snapshot. */
  executeSell: (args: ExecuteSellArgs) => void;
  /** Run `fn` behind the same login + first-trade-terms gate (the form's swap leg). */
  withTradeGate: (fn: () => void, confettiOrigin?: ConfettiOrigin) => void;
  /** Open the login flow, capturing the return-to URL (the form's Connect CTA). */
  requestLogin: () => void;
  /** The form's swap mutation stays local (no chip path), so it reports its
   *  pending state here — folding swaps into the shared execution gate. */
  registerSwapPending: (pending: boolean) => void;
  /** Includes every outstanding order, even when a newer order has settled. */
  isTradePending: boolean;
  isBuySellBlocked: boolean;
}

// ── Context ──────────────────────────────────────────────────────────────────

const TradeExecutorContext: Context<TradeExecutor | null> = createContext<TradeExecutor | null>(null);

/** Nullable read — the position card tolerates a missing provider (the
 *  fixture-driven workspace showcase renders the card with no trade host). */
export function useTradeExecutor(): TradeExecutor | null {
  return use(TradeExecutorContext);
}

/** Required read — the trade form cannot function without an executor. */
export function useRequiredTradeExecutor(): TradeExecutor {
  const executor = use(TradeExecutorContext);
  if (executor === null) {
    throw new Error('useRequiredTradeExecutor must be used inside <TradeExecutorProvider>');
  }
  return executor;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function TradeExecutorProvider(t0) {
  const $ = _c(56);
  const {
    children
  } = t0;
  const {
    activeWallet
  } = useActiveWallet();
  const {
    user
  } = useAuth();
  const {
    handleLogin
  } = useSdkTurnkey();
  const signingDeps = useTurnkeySigningDeps(getExternalWalletProvider);
  const thesisDialogOpen = useThesisDialogStore(_temp);
  const buyMutation = useBuyTrade();
  const sellMutation = useSellTrade();
  const swapPending = useTradeSubmissionLockStore(_temp2);
  const outstandingCount = useTradeSubmissionLockStore(_temp3);
  const hasUnknownTrade = useTradeSubmissionLockStore(_temp5);
  const allowRapidTrades = useAllowRapidTrades();
  useUnknownTradeStatus();
  useRecoverTradeTelemetry(user?.id ?? null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function registerSwapPending(pending) {
      if (useTradeSubmissionLockStore.getState().swapPending === pending) {
        return;
      }
      useTradeSubmissionLockStore.setState({
        swapPending: pending
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const registerSwapPending = t1;
  const isBuySellPending = buyMutation.isPending || sellMutation.isPending;
  const isTradePending = isBuySellPending || outstandingCount > 0 || swapPending;
  const isBuySellBlocked = swapPending || hasUnknownTrade || !allowRapidTrades && isTradePending;
  const hasAcceptedTerms = useTradeTermsAcceptanceStore(_temp6);
  const markTermsAccepted = useTradeTermsAcceptanceStore(_temp7);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const pendingTradeRef = useRef(null);
  const {
    pendingConfettiOriginRef,
    pendingViralSellRef
  } = useTradeCelebration();
  const needsLogin = user === null || !activeWallet;
  let t2;
  if ($[1] !== handleLogin || $[2] !== pendingConfettiOriginRef) {
    t2 = function requestLogin() {
      pendingTradeRef.current = null;
      pendingConfettiOriginRef.current = null;
      captureReturnToAfterAuth();
      handleLogin();
    };
    $[1] = handleLogin;
    $[2] = pendingConfettiOriginRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const requestLogin = t2;
  let t3;
  if ($[4] !== hasAcceptedTerms || $[5] !== needsLogin || $[6] !== pendingConfettiOriginRef || $[7] !== requestLogin || $[8] !== user) {
    t3 = function withTradeGate(run, confettiOrigin) {
      if (isTradeSubmissionBlocked()) {
        return;
      }
      if (needsLogin) {
        requestLogin();
        return;
      }
      const armed = () => {
        pendingConfettiOriginRef.current = confettiOrigin ?? null;
        run();
      };
      if (user !== null && !hasAcceptedTerms(user.id)) {
        if (pendingTradeRef.current !== null) {
          return;
        }
        pendingTradeRef.current = armed;
        setTermsDialogOpen(true);
        return;
      }
      armed();
    };
    $[4] = hasAcceptedTerms;
    $[5] = needsLogin;
    $[6] = pendingConfettiOriginRef;
    $[7] = requestLogin;
    $[8] = user;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const withTradeGate = t3;
  let t4;
  if ($[10] !== markTermsAccepted || $[11] !== user) {
    t4 = function handleTermsAccepted() {
      if (user === null) {
        return;
      }
      markTermsAccepted(user.id);
      const run_0 = pendingTradeRef.current;
      pendingTradeRef.current = null;
      run_0?.();
    };
    $[10] = markTermsAccepted;
    $[11] = user;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleTermsAccepted = t4;
  let t5;
  if ($[13] !== pendingConfettiOriginRef) {
    t5 = function handleTermsDialogOpenChange(open) {
      setTermsDialogOpen(open);
      if (!open && pendingTradeRef.current !== null) {
        pendingTradeRef.current = null;
        pendingConfettiOriginRef.current = null;
      }
    };
    $[13] = pendingConfettiOriginRef;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleTermsDialogOpenChange = t5;
  let executeBuy;
  if ($[15] !== activeWallet || $[16] !== buyMutation || $[17] !== signingDeps || $[18] !== withTradeGate) {
    executeBuy = function executeBuy(args) {
      withTradeGate(() => runBuy(args), args.confettiOrigin);
    };
    function runBuy(args_0) {
      if (!activeWallet || isTradeSubmissionBlocked()) {
        return;
      }
      const wallet = toWallet(activeWallet);
      submitGuardedTrade(() => runWithQuickTradeProgress(args_0.marketAddress, args_0.chip, onConfirmed => buyMutation.mutateAsync({
        marketAddress: args_0.marketAddress,
        outcomeIndex: args_0.outcomeIndex,
        outcomeMint: args_0.outcomeMint,
        amountInD6: args_0.amountInD6,
        walletDebitD6: args_0.walletDebitD6,
        feeExcludedQuoteInputD6: args_0.feeExcludedQuoteInputD6,
        outcomeTokensOutD6: args_0.outcomeTokensOutD6,
        desiredAmountD6: args_0.desiredAmountD6,
        slippageBps: args_0.slippageBps,
        suggestedSlippageBps: args_0.suggestedSlippageBps,
        marketPhase: args_0.marketPhase,
        ...(args_0.platformFeeD6 !== undefined ? {
          platformFeeD6: args_0.platformFeeD6
        } : {}),
        activeWallet: wallet,
        signingDeps,
        onConfirmed
      })));
    }
    $[15] = activeWallet;
    $[16] = buyMutation;
    $[17] = signingDeps;
    $[18] = withTradeGate;
    $[19] = executeBuy;
  } else {
    executeBuy = $[19];
  }
  let executeSell;
  if ($[20] !== activeWallet || $[21] !== pendingViralSellRef || $[22] !== sellMutation || $[23] !== signingDeps || $[24] !== withTradeGate) {
    executeSell = function executeSell(args_1) {
      withTradeGate(() => runSell(args_1), args_1.confettiOrigin);
    };
    function runSell(args_2) {
      if (!activeWallet || isTradeSubmissionBlocked()) {
        return;
      }
      const wallet_0 = toWallet(activeWallet);
      submitGuardedTrade(() => runWithQuickTradeProgress(args_2.marketAddress, args_2.chip, onConfirmed_0 => sellMutation.mutateAsync({
        marketAddress: args_2.marketAddress,
        ...(args_2.marketTitle !== undefined ? {
          marketTitle: args_2.marketTitle
        } : {}),
        outcomeIndex: args_2.outcomeIndex,
        outcomeMint: args_2.outcomeMint,
        tokensNeededD6: args_2.tokensNeededD6,
        expectedNetUsdcOutD6: args_2.expectedNetUsdcOutD6,
        grossQuoteAmountD6: args_2.grossQuoteAmountD6,
        feeExcludedQuoteOutputD6: args_2.feeExcludedQuoteOutputD6,
        sellPenaltyFeeD6: args_2.sellPenaltyFeeD6,
        positionAccounting: args_2.positionAccounting,
        slippageBps: args_2.slippageBps,
        suggestedSlippageBps: args_2.suggestedSlippageBps,
        marketPhase: args_2.marketPhase,
        ...(args_2.platformFeeD6 !== undefined ? {
          platformFeeD6: args_2.platformFeeD6
        } : {}),
        activeWallet: wallet_0,
        signingDeps,
        onConfirmed: onConfirmed_0
      })));
      capturePendingViralSell(args_2);
    }
    function capturePendingViralSell(args_3) {
      const walletPubkey = activeWallet?.address;
      const accounting = computeCheckedSellPositionAccounting({
        positionAccounting: args_3.positionAccounting,
        tokenAmountD6: args_3.tokensNeededD6.toString(),
        netQuoteProceedsD6: args_3.expectedNetUsdcOutD6.toString(),
        isAmm: args_3.marketPhase === "amm"
      });
      if (walletPubkey === undefined || !accounting.available) {
        pendingViralSellRef.current = null;
        return;
      }
      const proceedsD6 = BigInt(args_3.expectedNetUsdcOutD6.toString());
      pendingViralSellRef.current = {
        proceedsD6,
        accounting: accounting.value,
        marketPubkey: args_3.marketAddress,
        walletPubkey,
        outcomeIndex: args_3.outcomeIndex,
        marketTitle: args_3.marketTitle ?? "",
        outcomeLabel: args_3.outcomeLabel
      };
    }
    $[20] = activeWallet;
    $[21] = pendingViralSellRef;
    $[22] = sellMutation;
    $[23] = signingDeps;
    $[24] = withTradeGate;
    $[25] = executeSell;
  } else {
    executeSell = $[25];
  }
  let t6;
  if ($[26] !== buyMutation || $[27] !== sellMutation || $[28] !== thesisDialogOpen) {
    t6 = () => {
      if (!thesisDialogOpen && (buyMutation.isSuccess || sellMutation.isSuccess)) {
        buyMutation.reset();
        sellMutation.reset();
      }
    };
    $[26] = buyMutation;
    $[27] = sellMutation;
    $[28] = thesisDialogOpen;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== buyMutation.isSuccess || $[31] !== buyMutation.reset || $[32] !== sellMutation.isSuccess || $[33] !== sellMutation.reset || $[34] !== thesisDialogOpen) {
    t7 = [buyMutation.isSuccess, sellMutation.isSuccess, buyMutation.reset, sellMutation.reset, thesisDialogOpen];
    $[30] = buyMutation.isSuccess;
    $[31] = buyMutation.reset;
    $[32] = sellMutation.isSuccess;
    $[33] = sellMutation.reset;
    $[34] = thesisDialogOpen;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  useEffect(t6, t7);
  const wasBuySellPendingRef = useRef(false);
  let t8;
  let t9;
  if ($[36] !== isBuySellPending || $[37] !== pendingConfettiOriginRef || $[38] !== pendingViralSellRef) {
    t8 = () => {
      if (isBuySellPending) {
        wasBuySellPendingRef.current = true;
      } else {
        if (wasBuySellPendingRef.current) {
          wasBuySellPendingRef.current = false;
          pendingConfettiOriginRef.current = null;
          pendingViralSellRef.current = null;
        }
      }
    };
    t9 = [isBuySellPending, pendingConfettiOriginRef, pendingViralSellRef];
    $[36] = isBuySellPending;
    $[37] = pendingConfettiOriginRef;
    $[38] = pendingViralSellRef;
    $[39] = t8;
    $[40] = t9;
  } else {
    t8 = $[39];
    t9 = $[40];
  }
  useEffect(t8, t9);
  let t10;
  if ($[41] !== executeBuy || $[42] !== executeSell || $[43] !== isBuySellBlocked || $[44] !== isTradePending || $[45] !== requestLogin || $[46] !== withTradeGate) {
    t10 = {
      executeBuy,
      executeSell,
      withTradeGate,
      requestLogin,
      registerSwapPending,
      isTradePending,
      isBuySellBlocked
    };
    $[41] = executeBuy;
    $[42] = executeSell;
    $[43] = isBuySellBlocked;
    $[44] = isTradePending;
    $[45] = requestLogin;
    $[46] = withTradeGate;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  const executor = t10;
  let t11;
  if ($[48] !== handleTermsAccepted || $[49] !== handleTermsDialogOpenChange || $[50] !== termsDialogOpen) {
    t11 = <AcceptTradeTermsDialog open={termsDialogOpen} onOpenChange={handleTermsDialogOpenChange} onAccept={handleTermsAccepted} />;
    $[48] = handleTermsAccepted;
    $[49] = handleTermsDialogOpenChange;
    $[50] = termsDialogOpen;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== children || $[53] !== executor || $[54] !== t11) {
    t12 = <TradeExecutorContext.Provider value={executor}>{children}{t11}</TradeExecutorContext.Provider>;
    $[52] = children;
    $[53] = executor;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  return t12;
}
function _temp7(s_4) {
  return s_4.markAccepted;
}
function _temp6(s_3) {
  return s_3.hasAccepted;
}
function _temp5(s_2) {
  return s_2.outstanding.some(_temp4);
}
function _temp4(trade) {
  return trade.status === "unknown";
}
function _temp3(s_1) {
  return s_1.outstanding.length;
}
function _temp2(s_0) {
  return s_0.swapPending;
}
function _temp(s) {
  return s.open;
}
function toWallet(w: ActiveWallet): ActiveWallet {
  return {
    id: w.id,
    address: w.address,
    type: w.type
  };
}